import { createClient } from '@supabase/supabase-js';
import { getActiveOrganisationId } from './activeOrg';
import { extractRawIdToken, isJwtExpired } from './authToken';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_KEY) {
  throw new Error(
    'Missing Supabase config. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.'
  );
}

export { SUPABASE_URL, SUPABASE_KEY };

let getIdTokenClaimsFn = async () => undefined;

/**
 * Wire Auth0 ID token into Supabase (third-party Auth0 integration).
 * @typedef {import('./types/database').Profile} Profile
 * @typedef {import('./types/database').StageGate} StageGate
 * @typedef {import('./types/database').GatePhaseDeliverable} GatePhaseDeliverable
 * @typedef {import('./types/database').OrgGateDeliverableTemplate} OrgGateDeliverableTemplate
 */
export function setAuth0IdTokenGetter(fn) {
  getIdTokenClaimsFn = fn || (async () => undefined);
}

/**
 * Shared Supabase client (Auth0 third-party JWT).
 * Typed rows: see `types/database.js`. Regenerate full schema: `npm run gen:types`
 * @type {import('@supabase/supabase-js').SupabaseClient}
 */
export const sb = createClient(SUPABASE_URL, SUPABASE_KEY, {
  accessToken: async () => {
    try {
      const claims = await getIdTokenClaimsFn();
      const raw = extractRawIdToken(claims);
      if (!raw || isJwtExpired(raw, 0)) return undefined;
      return raw;
    } catch {
      return undefined;
    }
  },
  global: {
    // One identity can now hold a profile row in more than one organisation
    // (multi-org membership). RLS can't tell those rows apart on its own, so
    // every request carries which org the user is currently acting as — read
    // fresh on each call so a mid-session org switch takes effect immediately.
    // The database only trusts this header after confirming a profiles row
    // actually ties this identity to that org (see current_active_org_id()).
    fetch: (input, init) => {
      const activeOrgId = getActiveOrganisationId();
      if (!activeOrgId) return window.fetch(input, init);
      const headers = new Headers(init?.headers);
      headers.set('x-active-org', activeOrgId);
      return window.fetch(input, { ...init, headers });
    },
  },
});
